import React, { useEffect, useState } from "react";
import {
    Box,
    VStack,
    HStack,
    Heading,
    Text,
    Container,
    Flex,
    Link,
    Image,
    Badge,
    SimpleGrid,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Spinner,
    Alert,
    AlertIcon,
} from '@chakra-ui/react';
import { useNavigate } from "react-router-dom";
import { SearchIcon } from '@chakra-ui/icons';
import FullScreenTemplate from "../../templates/FullScreenTemplate";
import LargeWithNewsletter from "../../components/Reuseable/LargeWithNewsletter";
import { updatePageSEO, seoConfigs } from '../../utils/seo-helpers';

export default function BlogList() {
    const [blogPosts, setBlogPosts] = useState([]);
    const [filteredPosts, setFilteredPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [categories, setCategories] = useState([]);
    const navigate = useNavigate();

    // Update SEO for blog list page
    useEffect(() => {
        updatePageSEO(seoConfigs.blog);
    }, []);

    // Search function that works with loaded blog posts
    const searchBlogPosts = (posts, query) => {
        if (!query.trim()) return posts;
        
        const searchTerm = query.toLowerCase();
        return posts.filter(post => 
            post.title.toLowerCase().includes(searchTerm) ||
            (post.subtitle && post.subtitle.toLowerCase().includes(searchTerm)) ||
            (post.metaDescription && post.metaDescription.toLowerCase().includes(searchTerm)) ||
            (post.tags && post.tags.some(tag => tag.toLowerCase().includes(searchTerm))) ||
            (post.author && post.author.toLowerCase().includes(searchTerm))
        );
    };

    useEffect(() => {
        const loadBlogPosts = async () => {
            try {
                setLoading(true);
                
                // Fetch blog posts from API
                const response = await fetch('/api/blog-posts');
                let apiPosts = [];
                
                if (response.ok) {
                    apiPosts = await response.json();
                }
                
                // Add static blog posts
                const staticPosts = [
                    
                ];

                const allBlogPosts = [...staticPosts, ...apiPosts];
                setBlogPosts(allBlogPosts);
                setFilteredPosts(allBlogPosts);

                // Extract unique categories
                const uniqueCategories = [...new Set(allBlogPosts.map(post => post.category))];
                setCategories(uniqueCategories);

            } catch (error) {
                console.error('Error loading blog posts:', error);
            } finally {
                setLoading(false);
            }
        };

        loadBlogPosts();
    }, []);

    useEffect(() => {
        let filtered = blogPosts;

        // Filter by search query
        if (searchQuery) {
            filtered = searchBlogPosts(filtered, searchQuery);
        }

        // Filter by category
        if (selectedCategory !== 'all') {
            filtered = filtered.filter(post => post.category === selectedCategory);
        }

        setFilteredPosts(filtered);
    }, [searchQuery, selectedCategory, blogPosts]);

    const handlePostClick = (slug) => {
        navigate(`/blog/${slug}`);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <FullScreenTemplate>
                <Container maxW="container.xl" py={8}>
                    <Flex justify="center" align="center" minH="400px">
                        <Spinner size="xl" color="orange.500" />
                    </Flex>
                </Container>
            </FullScreenTemplate>
        );
    }

    return (
        <div>
        <FullScreenTemplate>
            <Container maxW="container.xl" py={8}>
                {/* Header */}
                <Box textAlign="center" mb={12}>
                <Heading
  as="h1"
  fontSize="5xl"
  mb={8}
  fontWeight="700"
  bgGradient="linear(to-r, purple.500, pink.500)"
  bgClip="text"
  color="transparent"
>
  Our Blog
</Heading>

                    {/* <Heading as="h1" size="2xl" mb={8} color="gray.800">
                        The Honeybear Blog
                    </Heading> */}
                    <Text fontSize="lg" color="gray.600" maxW="2xl" mx="auto">
                        Discover insights, tips, and guides about AI document analysis and AI tooling in the real world.
                    </Text>
                </Box>

                {/* Search and Filter */}
                <Box mb={8}>
                    <Flex direction={{ base: "column", md: "row" }} gap={4}>
                        <InputGroup maxW={{ base: "full", md: "400px" }}>
                            <InputLeftElement pointerEvents="none">
                                <SearchIcon color="gray.400" />
                            </InputLeftElement>
                            <Input
                                placeholder="Search blog posts..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                borderRadius="lg"
                            />
                        </InputGroup>
                        
                        <Select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            maxW={{ base: "full", md: "200px" }}
                            borderRadius="lg"
                        >
                            <option value="all">All Categories</option>
                            {categories.map(category => (
                                <option key={category} value={category}>
                                    {category}
                                </option>
                            ))}
                        </Select>
                    </Flex>
                </Box>

                {/* Blog Posts Grid */}
                {filteredPosts.length === 0 ? (
                    <Alert status="info">
                        <AlertIcon />
                        {searchQuery || selectedCategory !== 'all' 
                            ? `No blog posts found matching "${searchQuery}"${selectedCategory !== 'all' ? ` in ${selectedCategory}` : ''}.`
                            : 'No blog posts available.'
                        }
                    </Alert>
                ) : (
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
                        {filteredPosts.map((post, index) => (
                            <Box
                                key={post.slug}
                                border="1px solid"
                                borderColor="gray.200"
                                borderRadius="lg"
                                overflow="hidden"
                                transition="all 0.3s ease"
                                _hover={{
                                    transform: "translateY(-4px)",
                                    boxShadow: "lg",
                                    borderColor: "orange.300"
                                }}
                                cursor="pointer"
                                onClick={() => handlePostClick(post.slug)}
                            >
                                {post.featuredImage && (
                                    <Image
                                        src={post.featuredImage}
                                        alt={post.title}
                                        width="100%"
                                        height="200px"
                                        objectFit="cover"
                                    />
                                )}
                                
                                <Box p={6}>
                                    <HStack mb={3} spacing={2}>
                                        <Badge colorScheme="orange" variant="subtle">
                                            {post.category}
                                        </Badge>
                                        {post.source === 'outrank.so' && (
                                            <Badge colorScheme="blue" variant="subtle">
                                                Blog
                                            </Badge>
                                        )}
                                    </HStack>
                                    
                                    <Heading as="h3" size="md" mb={3} color="gray.800">
                                        {post.title}
                                    </Heading>
                                    
                                    {post.subtitle && (
                                        <Text color="gray.600" mb={4} noOfLines={2}>
                                            {post.subtitle}
                                        </Text>
                                    )}
                                    
                                    <VStack align="start" spacing={2}>
                                        <Text fontSize="sm" color="gray.500">
                                            {formatDate(post.publishDate)}
                                        </Text>
                                        <Text fontSize="sm" color="gray.500">
                                            By {post.author}
                                        </Text>
                                    </VStack>
                                    
                                    {post.tags && post.tags.length > 0 && (
                                        <HStack mt={4} flexWrap="wrap">
                                            {post.tags.slice(0, 3).map(tag => (
                                                <Badge key={tag} colorScheme="gray" variant="outline" size="sm">
                                                    {tag}
                                                </Badge>
                                            ))}
                                        </HStack>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </SimpleGrid>
                )}
            </Container>
            <Box height="auto" width="100%">
                    <LargeWithNewsletter />
                </Box>
        </FullScreenTemplate>
        </div>
    );
} 